/*
 * Credentials (U23, restricted in U3).
 *
 * Per-brand credentials for admanage / Notion / Google. The secret is NEVER
 * shown — each card surfaces only the non-secret metadata (kind, key version,
 * last-rotated). Only the platform owner (super-admin) can reach this page;
 * the route guard (`RequireSuperAdmin`) blocks non-super-admins before render.
 *
 * Credential access (reads and writes) is gated to the super-admin (`isSuperAdmin`)
 * and ENFORCED server-side (ALL credential endpoints are super-admin-only).
 */

import { useState } from "react";
import { Box } from "@mantine/core";
import {
  Alert,
  Button,
  EmptyState,
  Mono,
  PageHeader,
} from "../components/ui";
import { useAuth } from "../auth/AuthContext";
import { errorMessage } from "../api/errors";
import {
  useCredentials,
  useCredentialKinds,
  useCredentialPublicFields,
  useUpsertCredential,
  useRotateCredential,
  useDeleteCredential,
} from "../components/config/hooks";
import {
  CredentialForm,
  type CredentialMode,
} from "../components/config/CredentialForm";
import type {
  CredentialResponse,
  CredentialKindDescriptor,
} from "../components/config/hooks";
import { Section } from "../components/config/Section";
import { formatDateTime } from "../lib/format";
import styles from "../components/config/config.module.css";

const DESCRIPTION =
  "admanage, Notion, and Google credentials for this brand. Secrets are write-only — only their configured state is shown.";

/** Build a kind → human label lookup from the registry descriptors. */
function makeKindLabel(kinds: CredentialKindDescriptor[]) {
  return (kind: string): string =>
    kinds.find((k) => k.kind === kind)?.label ?? kind;
}

function CredentialCard({
  cred,
  canWrite,
  kindLabel,
  onRotate,
  onDelete,
  deleting,
}: {
  cred: CredentialResponse;
  canWrite: boolean;
  kindLabel: (kind: string) => string;
  onRotate: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <Section title={kindLabel(cred.kind)}>
      <span className={styles.credState}>
        <span className={styles.credDot} aria-hidden="true" />
        Configured · rotated {formatDateTime(cred.last_rotated_at)}
      </span>
      <div className={styles.credMeta}>
        <div className={styles.credMetaRow}>
          <span>Kind</span>
          <strong>
            <Mono>{cred.kind}</Mono>
          </strong>
        </div>
        <div className={styles.credMetaRow}>
          <span>Key version</span>
          <strong>v{cred.key_version}</strong>
        </div>
      </div>
      <p className={styles.note}>The secret is never displayed.</p>
      {canWrite && (
        <div className={styles.credActions}>
          <Button variant="secondary" size="sm" onClick={onRotate}>
            Rotate
          </Button>
          <Button
            variant="danger"
            size="sm"
            loading={deleting}
            onClick={() => {
              if (
                window.confirm(
                  `Delete the ${kindLabel(cred.kind)} credential? Launches that need it will fail until re-added.`,
                )
              ) {
                onDelete();
              }
            }}
          >
            Delete
          </Button>
        </div>
      )}
    </Section>
  );
}

export function CredentialsPage() {
  const { isSuperAdmin } = useAuth();
  // Credential writes (add/rotate/delete) are super-admin-only — enforced
  // server-side, gated here. A plain brand admin sees read-only masked state.
  const canWrite = isSuperAdmin;
  const credsQuery = useCredentials();
  const kindsQuery = useCredentialKinds();
  const upsert = useUpsertCredential();
  const rotate = useRotateCredential();
  const del = useDeleteCredential();
  const [form, setForm] = useState<CredentialMode | null>(null);

  const creds = credsQuery.data ?? [];
  const kinds = kindsQuery.data ?? [];
  const kindLabel = makeKindLabel(kinds);
  const existingKinds = creds.map((c) => c.kind);
  const deletingKind = del.isPending ? del.variables : null;

  // On rotate, pre-fill the credential's PUBLIC (non-secret) field values.
  // Gated to a rotate form being open; a 404/failure is treated as "no prefill"
  // by the form (never surfaced as a fatal state).
  const rotateKind = form?.type === "rotate" ? form.kind : "";
  const publicFieldsQuery = useCredentialPublicFields(
    rotateKind,
    rotateKind.length > 0,
  );

  function closeForm() {
    setForm(null);
    upsert.reset();
    rotate.reset();
  }

  const saving = upsert.isPending || rotate.isPending;
  const saveError = upsert.error ?? rotate.error;

  if (credsQuery.isLoading) {
    return (
      <>
        <PageHeader title="Credentials" description={DESCRIPTION} />
        <p className={styles.note}>Loading credentials…</p>
      </>
    );
  }

  if (credsQuery.isError) {
    return (
      <>
        <PageHeader title="Credentials" description={DESCRIPTION} />
        <Alert tone="error">{errorMessage(credsQuery.error)}</Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Credentials"
        description={DESCRIPTION}
        actions={
          canWrite && form === null ? (
            <Button variant="primary" onClick={() => setForm({ type: "add" })}>
              Add credential
            </Button>
          ) : undefined
        }
      />

      {del.isError && <Alert tone="error">{errorMessage(del.error)}</Alert>}

      {form && (
        <Box mb="lg" maw={560}>
          <Section
            title={form.type === "rotate" ? "Rotate credential" : "Add credential"}
            description={
              form.type === "rotate"
                ? "Replace the stored secret. Non-secret fields are pre-filled from the stored values."
                : "Store a new credential for this brand. The secret is encrypted server-side."
            }
          >
            <CredentialForm
              mode={form}
              kinds={kinds}
              kindsLoading={kindsQuery.isLoading}
              kindsError={kindsQuery.isError}
              onReloadKinds={() => void kindsQuery.refetch()}
              existingKinds={existingKinds}
              publicFields={
                form.type === "rotate" ? publicFieldsQuery.data : undefined
              }
              saving={saving}
              saveError={saveError}
              onCancel={closeForm}
              onSubmit={({ kind, fields }) => {
                if (form.type === "rotate") {
                  rotate.mutate(
                    {
                      kind,
                      body: {
                        fields,
                      },
                    },
                    { onSuccess: closeForm },
                  );
                } else {
                  upsert.mutate(
                    { kind, body: { kind, fields } },
                    { onSuccess: closeForm },
                  );
                }
              }}
            />
          </Section>
        </Box>
      )}

      {creds.length === 0 && form === null ? (
        <EmptyState
          icon="⚿"
          title="No credentials set"
          description={
            canWrite
              ? "Add the admanage, Notion, and Google credentials this brand needs to launch."
              : "The platform owner has not configured any credentials for this brand yet."
          }
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => setForm({ type: "add" })}>
                Add credential
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className={styles.credGrid}>
          {creds.map((cred) => (
            <CredentialCard
              key={cred.id}
              cred={cred}
              canWrite={canWrite}
              kindLabel={kindLabel}
              deleting={deletingKind === cred.kind}
              onRotate={() => {
                upsert.reset();
                rotate.reset();
                setForm({ type: "rotate", kind: cred.kind });
              }}
              onDelete={() => del.mutate(cred.kind)}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default CredentialsPage;
